import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { type BillingUnitDto, useGetBillingUnits } from '@dhl/mdp-api'
import { EditSolidIcon } from '@dhl-ui/icons'
import { type GridColDef, Pagination } from '@dhl/components'
import { Box, Grid, IconButton, Paper } from '@dhl-ui/components'

import { links } from '~/AppRoutes'
import { AdminDataGrid, lowercaseTransform, SearchTextField, useSearchAndPage } from '~/common'

export const BillingunitsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { searchAndPageParams, handleSearch, page, pageSize, setPage, setPageSize, search } = useSearchAndPage()

  const { data, isFetching } = useGetBillingUnits(searchAndPageParams)

  const handleRowClick = (row: BillingUnitDto) => navigate(links.billingUnitsDetailsLink(row?.id?.toString() || ''))

  const onClickEdit = (row: BillingUnitDto) => navigate(links.billingUnitsEditLink(row?.id?.toString() || ''))

  const renderCellEdit = (row: BillingUnitDto) => {
    return (
      <IconButton aria-label="edit billing units" onClick={() => onClickEdit(row)}>
        <EditSolidIcon data-testid="edit-billingunits" />
      </IconButton>
    )
  }
  const columns: GridColDef[] = [
    {
      field: 'cccId',
      headerName: t('admin.billingUnitsPage.billingUnitsOverview.identifier'),
      flex: 1,
    },
    {
      field: 'name',
      headerName: t('admin.billingUnitsPage.billingUnitsOverview.name'),
      flex: 1,
    },
    {
      field: 'actions',
      headerName: t('admin.billingUnitsPage.billingUnitsOverview.actions'),
      sortable: false,
      flex: 1,
      renderCell: ({ row }) => renderCellEdit(row),
      disableReorder: true,
      disableExport: true,
      resizable: false,
      filterable: false,
      editable: false,
    },
  ]

  return (
    <>
      <Box sx={{ flexGrow: 1 }} data-testid="billing-units-page">
        <Box mb={3}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid>
              <SearchTextField onChange={handleSearch} transform={lowercaseTransform} />
            </Grid>
          </Grid>
        </Box>
        <Box mt={2}>
          <Paper>
            <AdminDataGrid<BillingUnitDto>
              loading={isFetching}
              data-testid="billingunits-table"
              autoHeight
              rows={data?.content ?? []}
              columns={columns}
              onRowClick={({ row }) => handleRowClick(row)}
              rowHeight={48}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'name', sort: 'asc' }],
                },
              }}
              columnHeaderHeight={48}
              disableRowSelectionOnClick
              disableColumnFilter
              hideFooter
            />
          </Paper>
          <Box mt={2}>
            <Pagination
              page={page}
              count={data?.totalCount ?? 0}
              onPageChange={(_, page) => {
                setPage(page)
              }}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(pageSize) => {
                setPageSize(pageSize)
              }}
            />
          </Box>
        </Box>
      </Box>
    </>
  )
}
